import React, { useEffect, useState, Fragment } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams, useNavigate } from "react-router-dom";
import { Container, Row, Col, Table, Button } from "react-bootstrap";
import PageTitle from "../components/PageTitle";
import { Calender, DateFormat, DropDown } from "../components/Forms";
import { Buttons } from "../components/Buttons";
import {
  fetchGroupedBalanceSheet,
  deleteBalanceSheet,
} from "../slices/balanceSheetSlice";
import { fetchAgent } from "../slices/agentSlice";
import NotifyData from "../components/NotifyData";
import Dialog from "../components/Dialog";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import * as XLSX from "xlsx";
const AgentBalanceSheet = () => {
  const { agentName } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { groupedBalanceSheet, totals } = useSelector(
    (state) => state.balanceSheet
  );
  const { agent } = useSelector((state) => state.agent);
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [nameFilter, setNameFilter] = useState(decodeURIComponent(agentName)); // Pre-filter by agent
  const [typeFilter, setTypeFilter] = useState("All");
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [selectedItemToDelete, setSelectedItemToDelete] = useState(null);
  useEffect(() => {
    dispatch(fetchGroupedBalanceSheet({ fromDate, toDate, nameFilter }));
  }, [dispatch, fromDate, toDate, nameFilter]);
  useEffect(() => {
    dispatch(fetchAgent());
  }, [dispatch]);
  // Flatten all details
  const allDetails = groupedBalanceSheet.flatMap((item) =>
    item.details.map((detail) => ({
      ...detail,
      date: item.date,
    }))
  );
  // Filter based on typeFilter
  const filteredDetails = allDetails.filter((detail) => {
    if (typeFilter === "All") return true;
    if (typeFilter === "Credit") return detail.credit > 0;
    if (typeFilter === "Debit") return detail.debit > 0;
    return true;
  });
  // Group filtered details by date for rowspan rendering
  const groupedDetails = filteredDetails.reduce((acc, detail) => {
    const date = detail.date;
    if (!acc[date]) {
      acc[date] = [];
    }
    acc[date].push(detail);
    return acc;
  }, {});
  // Define visible columns based on filter
  const showCreditColumn = typeFilter !== "Debit";
  const showDebitColumn = typeFilter !== "Credit";
  const visibleColumnCount =
    2 + (showCreditColumn ? 1 : 0) + (showDebitColumn ? 1 : 0); // date + credit/debit + desc + action
  const handleAdd = () => {
    // Navigate to create with prefilled agent
    navigate("/create-balance-sheet", {
      state: {
        isEdit: false,
        data: {
          agent_name: decodeURIComponent(agentName),
          agent_id: agent.find(
            (a) => a.agent_name === decodeURIComponent(agentName)
          )?.agent_id,
        },
      },
    });
  };
  const handleEdit = (detail) => {
    if (detail.type !== "credit") {
      NotifyData("Edit is only available for credit entries!", "error");
      return;
    }
    const editData = {
      balance_sheet_id: detail.balance_sheet_id,
      entry_date: detail.entry_date,
      agent_name: detail.agent_name,
      agent_id: detail.agent_id,
      amount: detail.credit,
      description: detail.description,
      type: detail.type,
    };
    navigate("/create-balance-sheet", {
      state: { isEdit: true, data: editData },
    });
  };
  const handleDelete = (detail) => {
    if (detail.type !== "credit") {
      NotifyData("Delete is only available for credit entries!", "error");
      return;
    }
    setSelectedItemToDelete(detail);
    setShowDeleteDialog(true);
  };
  const confirmDelete = async () => {
    if (selectedItemToDelete) {
      try {
        await dispatch(
          deleteBalanceSheet(selectedItemToDelete.balance_sheet_id)
        ).unwrap();
        NotifyData("Balance sheet entry deleted successfully!", "success");
        // Refetch after delete
        dispatch(fetchGroupedBalanceSheet({ fromDate, toDate, nameFilter }));
      } catch (error) {
        NotifyData(
          "Failed to delete balance sheet entry. Please try again!",
          "error"
        );
        console.error(error);
      }
    }
    setShowDeleteDialog(false);
    setSelectedItemToDelete(null);
  };
  const cancelDelete = () => {
    setShowDeleteDialog(false);
    setSelectedItemToDelete(null);
  };
  const handleDateChange = (type, date) => {
    if (type === "from") {
      setFromDate(date);
    } else {
      setToDate(date);
    }
  };
  // Helper to flatten details for exports (filtered)
  const getFlattenedDetails = () => {
    return filteredDetails;
  };
  // PDF Download - Flattened
  const handlePDFDownload = () => {
    const allDetails = getFlattenedDetails();
    if (allDetails.length === 0) {
      NotifyData("No data to export", "warn");
      return;
    }
    const doc = new jsPDF("p", "mm", "a4");
    const pageWidth = doc.internal.pageSize.getWidth();
    const margin = 10;
    let yPosition = 20;
    doc.setFontSize(18);
    doc.setFont("helvetica", "bold");
    doc.text(
      `Balance Sheet Report - ${decodeURIComponent(agentName)}`,
      pageWidth / 2,
      yPosition,
      { align: "center" }
    );
    yPosition += 10;
    if (fromDate && toDate) {
      doc.setFontSize(12);
      doc.setFont("helvetica", "normal");
      doc.text(`Period: ${fromDate} to ${toDate}`, pageWidth / 2, yPosition, {
        align: "center",
      });
      yPosition += 8;
    }
    if (typeFilter !== "All") {
      doc.text(`Type: ${typeFilter}`, pageWidth / 2, yPosition, {
        align: "center",
      });
      yPosition += 8;
    }
    yPosition += 5;
    const tableColumn = [
      { header: "Date", dataKey: "date" },
      ...(showCreditColumn
        ? [{ header: "Credit (Rs.)", dataKey: "credit" }]
        : []),
      ...(showDebitColumn ? [{ header: "Debit (Rs.)", dataKey: "debit" }] : []),
      { header: "Description", dataKey: "description" },
    ];
    const tableRows = allDetails.map((detail) => ({
      date: detail.date,
      credit: detail.credit.toFixed(2),
      debit: detail.debit.toFixed(2),
      description: detail.description,
    }));
    // Add total row
    const totalRow = {
      date: "Total",
      ...(showCreditColumn ? { credit: totals.paid.toFixed(2) } : {}),
      ...(showDebitColumn ? { debit: totals.debit.toFixed(2) } : {}),
      description: "",
    };
    tableRows.push(totalRow);
    autoTable(doc, {
      startY: yPosition,
      headStyles: {
        fillColor: [41, 128, 185],
        textColor: 255,
        fontStyle: "bold",
        halign: "center",
      },
      alternateRowStyles: {
        fillColor: [245, 245, 245],
      },
      columnStyles: {
        date: { halign: "center", cellWidth: 18 },
        ...(showCreditColumn
          ? { credit: { halign: "right", cellWidth: 20 } }
          : {}),
        ...(showDebitColumn
          ? { debit: { halign: "right", cellWidth: 20 } }
          : {}),
        description: { halign: "left", cellWidth: 70, overflow: "linebreak" },
      },
      styles: {
        fontSize: 7,
        cellPadding: 2,
        valign: "middle",
        lineColor: [0, 0, 0],
        lineWidth: 0.1,
        overflow: "linebreak",
      },
      rowStyles: {
        0: { fillColor: [41, 128, 185], textColor: 255 },
      },
      footStyles: {
        fillColor: [41, 128, 185],
        textColor: 255,
        fontStyle: "bold",
      },
      columns: tableColumn,
      body: tableRows,
      theme: "grid",
      margin: { left: margin, right: margin },
      tableWidth: "auto",
    });
    yPosition = doc.lastAutoTable.finalY + 10;
    doc.setFontSize(12);
    doc.setFont("helvetica", "bold");
    doc.text(
      `Net Balance: Rs. ${totals.balance.toFixed(2)}`,
      pageWidth / 2,
      yPosition,
      { align: "center" }
    );
    const pageCount = doc.internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setFontSize(8);
      doc.setFont("helvetica", "italic");
      doc.text(
        `Generated on: ${new Date().toLocaleDateString()} | Page ${i} of ${pageCount}`,
        pageWidth / 2,
        doc.internal.pageSize.getHeight() - 10,
        { align: "center" }
      );
    }
    doc.save(
      `balance-sheet-${decodeURIComponent(agentName)}-${
        new Date().toISOString().split("T")[0]
      }.pdf`
    );
  };
  // Excel Download - Flattened
  const handleExcelDownload = () => {
    const allDetails = getFlattenedDetails();
    if (allDetails.length === 0) {
      NotifyData("No data to export", "warn");
      return;
    }
    const headers = [
      "Date",
      ...(showCreditColumn ? ["Credit (Rs.)"] : []),
      ...(showDebitColumn ? ["Debit (Rs.)"] : []),
      "Description",
    ];
    const wsData = [
      [`Balance Sheet Report - ${decodeURIComponent(agentName)}`],
      [],
      ...(fromDate && toDate ? [[`Period: ${fromDate} to ${toDate}`]] : []),
      ...(typeFilter !== "All" ? [[`Type: ${typeFilter}`]] : []),
      [],
      headers,
    ];
    allDetails.forEach((detail) => {
      const detailRow = [
        detail.date,
        ...(showCreditColumn ? [detail.credit.toFixed(2)] : []),
        ...(showDebitColumn ? [detail.debit.toFixed(2)] : []),
        detail.description,
      ];
      wsData.push(detailRow);
    });
    // Add total row
    const totalRow = [
      "Total",
      ...(showCreditColumn ? [totals.paid.toFixed(2)] : []),
      ...(showDebitColumn ? [totals.debit.toFixed(2)] : []),
      "",
    ];
    wsData.push(totalRow);
    wsData.push([]);
    const balanceRow = ["Net Balance", "", totals.balance.toFixed(2), ""];
    wsData.push(balanceRow);
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.aoa_to_sheet(wsData);
    const colWidths = [
      { wch: 12 },
      ...(showCreditColumn ? [{ wch: 15 }] : []),
      ...(showDebitColumn ? [{ wch: 15 }] : []),
      { wch: 50 },
    ];
    ws["!cols"] = colWidths;
    // Style headers and totals
    const headerRow = headers.length + 5; // Adjust based on wsData (title, empty, period, type, empty, headers)
    headers.forEach((_, col) => {
      const cellRef = XLSX.utils.encode_cell({ c: col, r: headerRow });
      if (ws[cellRef]) {
        ws[cellRef].s = {
          font: { bold: true },
          alignment: { horizontal: "center" },
        };
      }
    });
    const totalRowIndex = wsData.length - 3;
    headers.forEach((_, col) => {
      const cellRef = XLSX.utils.encode_cell({ c: col, r: totalRowIndex });
      if (ws[cellRef]) {
        ws[cellRef].s = {
          font: { bold: true },
          alignment: { horizontal: "center" },
        };
      }
    });
    const titleCell = XLSX.utils.encode_cell({ c: 0, r: 0 });
    if (ws[titleCell]) {
      ws[titleCell].s = {
        font: { bold: true, sz: 16 },
        alignment: { horizontal: "center" },
      };
    }
    // Format numbers
    allDetails.forEach((_, rowIndex) => {
      const dataRowIndex = rowIndex + headerRow + 1; // Headers above
      const creditColIndex = 1; // After date
      const debitColIndex = showCreditColumn ? 2 : 1; // After date and possible credit
      if (showCreditColumn) {
        const cellRef = XLSX.utils.encode_cell({
          c: creditColIndex,
          r: dataRowIndex,
        });
        if (ws[cellRef]) {
          ws[cellRef].t = "n";
          ws[cellRef].z = "#,##0.00";
          ws[cellRef].s = {
            numFmt: "#,##0.00",
            alignment: { horizontal: "center" },
          };
        }
      }
      if (showDebitColumn) {
        const cellRef = XLSX.utils.encode_cell({
          c: debitColIndex,
          r: dataRowIndex,
        });
        if (ws[cellRef]) {
          ws[cellRef].t = "n";
          ws[cellRef].z = "#,##0.00";
          ws[cellRef].s = {
            numFmt: "#,##0.00",
            alignment: { horizontal: "center" },
          };
        }
      }
    });
    XLSX.utils.book_append_sheet(wb, ws, "Balance Sheet");
    XLSX.writeFile(
      wb,
      `balance-sheet-${decodeURIComponent(agentName)}-${
        new Date().toISOString().split("T")[0]
      }.xlsx`
    );
  };
  const renderActionButtons = (detail) => {
    if (detail.type !== "credit") {
      return <span className="text-muted"> - </span>;
    }
    return (
      <>
        <Button
          variant="outline-primary"
          size="sm"
          className="me-1"
          onClick={() => handleEdit(detail)}
        >
          Edit
        </Button>
        <Button
          variant="outline-danger"
          size="sm"
          onClick={() => handleDelete(detail)}
        >
          Delete
        </Button>
      </>
    );
  };
  const handleBack = () => {
    navigate(`/agent-invoices/${encodeURIComponent(agentName)}`);
  };
  return (
    <div id="main">
      <Container fluid>
        <Row>
          <Col lg="6" md="12" xs="12" className="py-3 align-self-center">
            <PageTitle
              PageTitle={`Balance Sheet - ${decodeURIComponent(agentName)}`}
              showButton={false}
            />
          </Col>
          <Col lg="6" md="12" xs="12" className="py-3">
            <div className="d-flex justify-content-end flex-wrap gap-2">
              <Buttons
                label="PDF"
                classname="crud-btn"
                OnClick={handlePDFDownload}
              />
              <Buttons
                label="Excel"
                classname="crud-btn"
                OnClick={handleExcelDownload}
              />
              <Buttons
                label="Add New"
                classname="crud-btn"
                OnClick={handleAdd}
              />
            </div>
          </Col>
          <Col lg="6" className="py-3">
            <div className="content-box">
              <Row>
                <Col lg="12">
                  <div className="d-flex justify-content-between align-items-center p-3 bg-light rounded">
                    <div>
                      <h5 className="mb-1">Balance</h5>
                      <p
                        className={`fw-bold mb-0 ${
                          totals.balance >= 0 ? "text-success" : "text-danger"
                        }`}
                      >
                        Rs. {totals.balance.toFixed(2)}
                      </p>
                    </div>
                  </div>
                </Col>
              </Row>
            </div>
          </Col>
          <Row className="mb-3">
            <Col lg="3" className="py-3">
              <DropDown
                textlabel="Filter by Type"
                options={[
                  { label: "All", value: "All" },
                  { label: "Credit", value: "Credit" },
                  { label: "Debit", value: "Debit" },
                ]}
                value={typeFilter}
                onChange={(e) => setTypeFilter(e.target.value)}
              />
            </Col>
            <Col lg="3" className="py-3">
              <Calender
                textlabel="From Date"
                value={fromDate}
                onChange={(date) => handleDateChange("from", date)}
              />
            </Col>
            <Col lg="3" className="py-3">
              <Calender
                textlabel="To Date"
                value={toDate}
                onChange={(date) => handleDateChange("to", date)}
              />
            </Col>
          </Row>
          <Col lg="12" xs="12">
            <div className="content-box">
              <Table bordered className="table-modern">
                <thead className="table-dark">
                  <tr>
                    <th>Date</th>
                    {showCreditColumn && <th>Credit</th>}
                    {showDebitColumn && <th>Debit</th>}
                    <th>Description</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(groupedDetails).map(([date, group]) => (
                    <Fragment key={date}>
                      {/* First row of the group with rowspan for date */}
                      <tr className="align-middle">
                        <td rowSpan={group.length} className="text-center">
                          <DateFormat date={date} />
                        </td>
                        {showCreditColumn && (
                          <td className="text-success text-center fw-semibold">
                            Rs. {group[0].credit.toFixed(2)}
                          </td>
                        )}
                        {showDebitColumn && (
                          <td className="text-danger text-center fw-semibold">
                            Rs. {group[0].debit.toFixed(2)}
                          </td>
                        )}
                        <td className="text-start ps-3">
                          {group[0].description}
                        </td>
                        <td className="text-center">
                          {renderActionButtons(group[0])}
                        </td>
                      </tr>
                      {/* Subsequent rows without date column */}
                      {group.slice(1).map((detail, idx) => (
                        <tr
                          key={detail.balance_sheet_id || idx}
                          className="align-middle"
                        >
                          {showCreditColumn && (
                            <td className="text-success text-center fw-semibold">
                              Rs. {detail.credit.toFixed(2)}
                            </td>
                          )}
                          {showDebitColumn && (
                            <td className="text-danger text-center fw-semibold">
                              Rs. {detail.debit.toFixed(2)}
                            </td>
                          )}
                          <td className="text-start ps-3">
                            {detail.description}
                          </td>
                          <td className="text-center">
                            {renderActionButtons(detail)}
                          </td>
                        </tr>
                      ))}
                    </Fragment>
                  ))}
                  {Object.keys(groupedDetails).length === 0 && (
                    <tr>
                      <td
                        colSpan={visibleColumnCount}
                        className="text-center py-4 text-muted"
                      >
                        No entries found for the selected date range.
                      </td>
                    </tr>
                  )}
                </tbody>
                <tfoot>
                  <tr className="table-dark fw-bold">
                    <td className="text-end">Total</td>
                    {showCreditColumn && (
                      <td className="text-success text-center">
                        Rs. {totals.paid.toFixed(2)}
                      </td>
                    )}
                    {showDebitColumn && (
                      <td className="text-danger text-center">
                        Rs. {totals.debit.toFixed(2)}
                      </td>
                    )}
                    <td></td>
                    <td></td>
                  </tr>
                </tfoot>
              </Table>
            </div>
          </Col>
        </Row>
      </Container>
      <Dialog
        DialogTitle="Confirm Delete"
        DialogBody="Are you sure you want to delete this balance sheet entry? This action cannot be undone."
        isVisible={showDeleteDialog}
        onConfirm={confirmDelete}
        onCancel={cancelDelete}
        confirmText="Delete"
        cancelText="Cancel"
      />
    </div>
  );
};
export default AgentBalanceSheet;
